import React, { useState, useEffect, useMemo } from 'react';
import { Download } from 'lucide-react';
import { AuthCheck } from './AuthCheck';
import { formatDisplayDate, getDateSortTime } from './lib/dateUtils';
import { buildTransactionVatEntries as buildTransactionVatEntriesForReporting } from './lib/vatReportingUtils';

const ReportExport = ({ 
  transactions, 
  categories, 
  buildTransactionVatEntries: buildTransactionVatEntriesProp,
  selectedTimeframe, 
  selectedCategory, 
  selectedType, 
  selectedPaymentMethods,
  selectedCategories,
  transactionTypeFilter,
  startDate, 
  endDate,
  vatAdjustments,
  user,
  handleGoogleSignIn,
  isCapitalExpense,
  reportTypes,
  initialReportType,
  dateLocale = 'en-GB'
}) => {
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  const safeVatAdjustments = useMemo(
    () => Array.isArray(vatAdjustments) ? vatAdjustments : [],
    [vatAdjustments]
  );

  const allowedReportTypes = useMemo(() => {
    const supportedTypes = new Set(['profitLoss', 'vat', 'filteredSummary', 'filteredTransactions']);
    const requestedTypes = Array.isArray(reportTypes) && reportTypes.length > 0
      ? reportTypes
      : ['filteredTransactions', 'filteredSummary', 'profitLoss', 'vat'];
    const filteredTypes = requestedTypes.filter((type) => supportedTypes.has(type));
    return filteredTypes.length > 0 ? filteredTypes : ['filteredTransactions', 'filteredSummary', 'profitLoss', 'vat'];
  }, [reportTypes]);

  const defaultReportType = useMemo(() => {
    if (typeof initialReportType === 'string' && allowedReportTypes.includes(initialReportType)) {
      return initialReportType;
    }
    return allowedReportTypes[0];
  }, [allowedReportTypes, initialReportType]);

  const [reportType, setReportType] = useState(defaultReportType);

  useEffect(() => {
    setReportType((previousType) => (
      allowedReportTypes.includes(previousType) ? previousType : defaultReportType
    ));
  }, [allowedReportTypes, defaultReportType]);

  const reportTypeLabels = {
    profitLoss: 'Profit & Loss Report',
    vat: 'VAT Report',
    filteredSummary: 'Summary',
    filteredTransactions: 'Transactions'
  };

  // Log transactions when they change
  useEffect(() => {
    console.log('[REPORT] Transactions provided to ReportExport component:', transactions?.length || 0);
  }, [transactions]);

  // Get filtered transactions - use pre-filtered from App.js, with option to include all types for P&L reports
  const getFilteredTransactions = (includeAllTypes = false) => {
    // Transactions are already filtered from App.js
    // Check if transactions exists and is an array
    if (!Array.isArray(transactions)) {
      console.warn('[REPORT] Transactions is not an array:', transactions);
      return [];
    }
  
    console.log('[REPORT] Using pre-filtered transactions:', transactions.length);
    
    // For P&L reports, we need both income and expenses, so we might need to adjust the filter
    // If includeAllTypes is true, return all transactions regardless of type filter
    if (includeAllTypes) {
      console.log('[REPORT] P&L report - including all transaction types');
      return transactions;
    }
    
    return transactions;
  };

  const buildTransactionVatEntries = React.useCallback((transaction, options = {}) => {
    if (typeof buildTransactionVatEntriesProp === 'function') {
      return buildTransactionVatEntriesProp(transaction, options);
    }
    return buildTransactionVatEntriesForReporting(transaction, options);
  }, [buildTransactionVatEntriesProp]);

  // Helper function to safely format numbers
  const safeFormatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return '0.00';
    }
    return Number(value).toFixed(decimals);
  };

  const csvEscapeCell = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    const escaped = stringValue.replace(/"/g, '""');
    return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
  };

  const toCsvRow = (values) => values.map(csvEscapeCell).join(',');

  const getCurrencySymbol = () => document.documentElement.dataset.currencySymbol || '\u20AC';

  const normalizeTransactionTypeKey = (value) => {
    const normalized = String(value || '').toLowerCase();
    return normalized === 'expense' ? 'expenses' : normalized;
  };

  const paymentMethodNames = {
    cash: 'Cash',
    card: 'Card',
    revolut: 'Revolut',
    transfer: 'Bank Transfer',
    voucher: 'Gift Voucher'
  };

  const normalizePaymentMethodId = (methodId) => {
    const rawMethod = String(methodId || '').trim().toLowerCase();
    const aliases = {
      bank_transfer: 'transfer',
      banktransfer: 'transfer',
      'bank transfer': 'transfer',
      gift_voucher: 'voucher',
      giftvoucher: 'voucher',
      'gift voucher': 'voucher'
    };
    return aliases[rawMethod] || rawMethod;
  };

  const getPaymentMethodLabel = (methodId) => {
    const normalizedMethod = normalizePaymentMethodId(methodId);
    if (!normalizedMethod) return 'Unknown';
    return paymentMethodNames[normalizedMethod] ||
      normalizedMethod
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (match) => match.toUpperCase());
  };

  const resolveCategoryName = (type, categoryId) => {
    const normalizedType = normalizeTransactionTypeKey(type);
    const categoryList = categories?.[normalizedType];
    if (Array.isArray(categoryList)) {
      const categoryMatch = categoryList.find((entry) => entry.id === categoryId);
      if (categoryMatch?.name) return categoryMatch.name;
    }
    return categoryId || 'Uncategorized';
  };

  const getSelectedCategoryNames = () => {
    if (!Array.isArray(selectedCategories) || selectedCategories.length === 0) {
      return [];
    }
    return selectedCategories.map((categoryId) => {
      const incomeMatch = Array.isArray(categories?.income)
        ? categories.income.find((entry) => entry.id === categoryId)
        : null;
      if (incomeMatch?.name) return incomeMatch.name;

      const expenseMatch = Array.isArray(categories?.expenses)
        ? categories.expenses.find((entry) => entry.id === categoryId)
        : null;
      return expenseMatch?.name || categoryId;
    });
  };

  const getSelectedPaymentMethodNames = () => {
    if (!Array.isArray(selectedPaymentMethods) || selectedPaymentMethods.length === 0) {
      return [];
    }
    return selectedPaymentMethods.map((methodId) => getPaymentMethodLabel(methodId));
  };

  const getReportPeriodLabel = () => {
    if (selectedTimeframe === 'custom' && startDate && endDate) {
      return `${formatDisplayDate(startDate, dateLocale)} to ${formatDisplayDate(endDate, dateLocale)}`;
    }
    return selectedTimeframe || 'all';
  };

  const appliedFiltersSummary = useMemo(() => {
    const normalizedTypeFilter = String(transactionTypeFilter || '').toLowerCase();
    const typeLabel = normalizedTypeFilter && normalizedTypeFilter !== 'all'
      ? normalizedTypeFilter
      : 'all';
    const categoryNames = getSelectedCategoryNames();
    const paymentMethodNamesSelected = getSelectedPaymentMethodNames();

    return {
      transactionType: typeLabel,
      categories: categoryNames.length ? categoryNames.join(' | ') : 'all',
      paymentMethods: paymentMethodNamesSelected.length ? paymentMethodNamesSelected.join(' | ') : 'all'
    };
  }, [categories, selectedCategories, selectedPaymentMethods, transactionTypeFilter]);

  const hasNonDateFiltersForProfitLoss = useMemo(() => {
    const hasTypeFilter = String(transactionTypeFilter || '').toLowerCase() !== 'all' && String(transactionTypeFilter || '').trim() !== '';
    const hasCategoryFilter = Array.isArray(selectedCategories) && selectedCategories.length > 0;
    const hasPaymentMethodFilter = Array.isArray(selectedPaymentMethods) && selectedPaymentMethods.length > 0;
    return hasTypeFilter || hasCategoryFilter || hasPaymentMethodFilter;
  }, [selectedCategories, selectedPaymentMethods, transactionTypeFilter]);

  const normalizeVatRate = (rawRate) => {
    const parsedRate = parseFloat(rawRate);
    if (!Number.isFinite(parsedRate) || parsedRate < 0) return null;
    return parsedRate > 1 ? parsedRate / 100 : parsedRate;
  };

  const formatVatRateLabel = (rate) => {
    const normalizedRate = normalizeVatRate(rate);
    return Number.isFinite(normalizedRate) ? `${(normalizedRate * 100).toFixed(1)}%` : 'No VAT';
  };

  const getTransactionVatAmount = (transaction) => {
    const vatEntries = buildTransactionVatEntries(transaction);
    if (vatEntries.length > 0) {
      const totalFromEntries = vatEntries.reduce((sum, entry) => sum + (Number(entry?.vatAmount) || 0), 0);
      return parseFloat(totalFromEntries.toFixed(2));
    }

    const explicitVatAmount = Number(transaction?.vatAmount);
    if (Number.isFinite(explicitVatAmount) && explicitVatAmount > 0) {
      return parseFloat(explicitVatAmount.toFixed(2));
    }

    return 0;
  };

  const getTransactionIncomeNetAmount = (transaction) => {
    const grossAmount = Number(transaction?.saleTotalAmount ?? transaction?.originalSaleAmount ?? transaction?.grossAmount ?? transaction?.amount) || 0;
    if (!Number.isFinite(grossAmount) || grossAmount <= 0) {
      return 0;
    }

    const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
    const netAmount = grossAmount - (Number.isFinite(vatAmount) && vatAmount > 0 ? vatAmount : 0);
    return parseFloat(Math.max(netAmount, 0).toFixed(2));
  };

  const getTransactionExpenseNetAmount = (transaction) => {
    const grossAmount = Number(
      transaction?.expenseTotalAmount ??
      transaction?.billTotalAmount ??
      transaction?.invoiceTotalAmount ??
      transaction?.grossAmount ??
      transaction?.amount
    ) || 0;
    if (!Number.isFinite(grossAmount) || grossAmount <= 0) {
      return 0;
    }

    const reclaimableVatAmount = transaction?.vatReclaimable === false
      ? 0
      : Number(getTransactionVatAmount(transaction) || 0);
    const netAmount = grossAmount - (Number.isFinite(reclaimableVatAmount) && reclaimableVatAmount > 0 ? reclaimableVatAmount : 0);
    return parseFloat(Math.max(netAmount, 0).toFixed(2));
  };

  // Generate profit and loss report data
  const generateProfitLossData = (filteredTransactions) => {
    // Group transactions by category
    const incomeByCategory = {};
    const expensesByCategory = {};
    const capitalExpensesByCategory = {};
    let totalIncome = 0;
    let totalRegularExpenses = 0;
    let totalCapitalExpenses = 0;
    let totalAnnualDepreciation = 0;

    // Process each transaction
    filteredTransactions.forEach(transaction => {
      const category = transaction.category || 'uncategorized';
      const typeKey = normalizeTransactionTypeKey(transaction.type);
      const amount = typeKey === 'income'
        ? getTransactionIncomeNetAmount(transaction)
        : typeKey === 'expenses'
          ? getTransactionExpenseNetAmount(transaction)
        : (parseFloat(transaction.amount) || 0);
      const categoryName = resolveCategoryName(typeKey, category);

      if (typeKey === 'income') {
        // Skip asset sales for regular income calculation
        if (category === 'assetSale') {
          return;
        }
        
        // Add to income
        if (!incomeByCategory[category]) {
          incomeByCategory[category] = {
            name: categoryName,
            amount: 0,
            transactions: []
          };
        }
        incomeByCategory[category].amount += amount;
        incomeByCategory[category].transactions.push(transaction);
        totalIncome += amount;
      } else if (typeKey === 'expenses') {
        // Check if this is a capital expense (asset purchase)
        const isCapitalAsset = isCapitalExpense ? isCapitalExpense(category) : 
          ['equipment', 'furniture', 'electronics', 'salon_tools', 'vehicle'].includes(category);
        
        if (isCapitalAsset) {
          // Add to capital expenses
          if (!capitalExpensesByCategory[category]) {
            capitalExpensesByCategory[category] = {
              name: categoryName,
              amount: 0,
              transactions: []
            };
          }
          capitalExpensesByCategory[category].amount += amount;
          capitalExpensesByCategory[category].transactions.push(transaction);
          totalCapitalExpenses += amount;
          
          // Add annual depreciation if available
          if (transaction.annualDepreciation) {
            totalAnnualDepreciation += parseFloat(transaction.annualDepreciation) || 0;
          } else {
            // Estimate depreciation if not provided (using 8 years as default period)
            const estimatedAnnualDepreciation = amount / 8;
            totalAnnualDepreciation += estimatedAnnualDepreciation;
          }
        } else {
          // Add to regular expenses
          if (!expensesByCategory[category]) {
            expensesByCategory[category] = {
              name: categoryName,
              amount: 0,
              transactions: []
            };
          }
          expensesByCategory[category].amount += amount;
          expensesByCategory[category].transactions.push(transaction);
          totalRegularExpenses += amount;
        }
      }
    });

    // Calculate net profit/loss (without depreciation)
    const grossNetProfit = totalIncome - totalRegularExpenses;
    
    // Calculate adjusted net profit (with depreciation)
    const adjustedNetProfit = totalIncome - (totalRegularExpenses + totalAnnualDepreciation);

    // Format data for CSV
    const reportData = {
      summary: {
        totalIncome,
        totalRegularExpenses,
        totalCapitalExpenses,
        totalAnnualDepreciation,
        grossNetProfit,
        adjustedNetProfit
      },
      incomeCategories: Object.values(incomeByCategory).sort((a, b) => b.amount - a.amount),
      expenseCategories: Object.values(expensesByCategory).sort((a, b) => b.amount - a.amount),
      capitalExpenseCategories: Object.values(capitalExpensesByCategory).sort((a, b) => b.amount - a.amount)
    };

    return reportData;
  };

  // Generate VAT report data
  const generateVatReportData = (filteredTransactions) => {
    // Group transactions by VAT rate
    const vatRateGroups = {};
    let totalVatCollected = 0;
    let totalVatPaid = 0;
    let totalVatReclaimable = 0;

    // Process each transaction
    filteredTransactions.forEach(transaction => {
      const vatEntries = buildTransactionVatEntries(transaction, { includeZeroVatEntries: true });
      if (!vatEntries.length) return;

      vatEntries.forEach((entry) => {
        const normalizedRate = normalizeVatRate(entry.rate);
        const vatRateKey = formatVatRateLabel(entry.rate);

        if (!vatRateGroups[vatRateKey]) {
          vatRateGroups[vatRateKey] = {
            rate: normalizedRate,
            rateFormatted: vatRateKey,
            salesAmount: 0,
            salesVat: 0,
            purchasesAmount: 0,
            purchasesVat: 0,
            reclaimableVat: 0,
            transactions: []
          };
        }

        const amount = parseFloat(entry.grossAmount) || 0;
        const vatAmount = parseFloat(entry.vatAmount) || 0;

        const typeKey = normalizeTransactionTypeKey(transaction.type);

        if (typeKey === 'income') {
          vatRateGroups[vatRateKey].salesAmount += amount;
          vatRateGroups[vatRateKey].salesVat += vatAmount;
          totalVatCollected += vatAmount;
        } else if (typeKey === 'expenses') {
          vatRateGroups[vatRateKey].purchasesAmount += amount;
          vatRateGroups[vatRateKey].purchasesVat += vatAmount;

          const isReclaimable = transaction.vatReclaimable !== false;
          if (isReclaimable) {
            vatRateGroups[vatRateKey].reclaimableVat += vatAmount;
            totalVatReclaimable += vatAmount;
          }

          totalVatPaid += vatAmount;
        }

        vatRateGroups[vatRateKey].transactions.push({
          ...transaction,
          _vatEntry: entry
        });
      });
    });

    // Calculate net VAT position
    const priorPeriodAdjustments = safeVatAdjustments.reduce(
      (sum, adjustment) => sum + (Number(adjustment?.deltaSignedVatAmount) || 0),
      0
    );
    const netVatPosition = totalVatCollected - totalVatReclaimable + priorPeriodAdjustments;

    // Format data for CSV
    const reportData = {
      summary: {
        totalVatCollected,
        totalVatPaid,
        totalVatReclaimable,
        priorPeriodAdjustments: parseFloat(priorPeriodAdjustments.toFixed(2)),
        netVatPosition
      },
      vatRateGroups: Object.values(vatRateGroups).sort((a, b) => b.rate - a.rate),
      adjustments: safeVatAdjustments
    };

    return reportData;
  };

  const generateFilteredSummaryData = (filteredTransactions) => {
    const incomeByCategory = {};
    const expensesByCategory = {};
    const paymentMethodTotals = {};

    let totalIncome = 0;
    let totalExpenses = 0;
    let transactionCount = 0;

    filteredTransactions.forEach((transaction) => {
      const amount = Number(transaction?.amount || 0);
      const typeKey = normalizeTransactionTypeKey(transaction?.type);
      if (!Number.isFinite(amount) || (typeKey !== 'income' && typeKey !== 'expenses')) {
        return;
      }

      transactionCount += 1;
      const categoryName = resolveCategoryName(typeKey, transaction?.category || '');
      if (typeKey === 'income') {
        totalIncome += amount;
        incomeByCategory[categoryName] = (incomeByCategory[categoryName] || 0) + amount;
      } else {
        totalExpenses += amount;
        expensesByCategory[categoryName] = (expensesByCategory[categoryName] || 0) + amount;
      }

      const paymentBreakdown = Array.isArray(transaction?.paymentBreakdown)
        ? transaction.paymentBreakdown
            .map((entry) => ({
              method: normalizePaymentMethodId(entry?.method),
              amount: Number(entry?.amount) || 0
            }))
            .filter((entry) => entry.method && entry.amount > 0)
        : [];

      const breakdownForSummary = paymentBreakdown.length > 0
        ? paymentBreakdown
        : [{
            method: normalizePaymentMethodId(transaction?.paymentMethod),
            amount
          }];

      breakdownForSummary.forEach((entry) => {
        const methodLabel = getPaymentMethodLabel(entry.method);
        paymentMethodTotals[methodLabel] = (paymentMethodTotals[methodLabel] || 0) + (Number(entry.amount) || 0);
      });
    });

    const mapToSortedEntries = (sourceMap) => Object.entries(sourceMap)
      .map(([name, value]) => ({
        name,
        value: parseFloat((Number(value) || 0).toFixed(2))
      }))
      .sort((left, right) => right.value - left.value);

    return {
      summary: {
        transactionCount,
        totalIncome: parseFloat(totalIncome.toFixed(2)),
        totalExpenses: parseFloat(totalExpenses.toFixed(2)),
        netPosition: parseFloat((totalIncome - totalExpenses).toFixed(2))
      },
      incomeCategories: mapToSortedEntries(incomeByCategory),
      expenseCategories: mapToSortedEntries(expensesByCategory),
      paymentMethods: mapToSortedEntries(paymentMethodTotals)
    };
  };

  // Convert profit and loss data to CSV
  const convertProfitLossToCSV = (data) => {
    const currencySymbol = getCurrencySymbol();
    const reportPeriod = getReportPeriodLabel();

    // Create CSV content
    let csvContent = [
      toCsvRow(['Profit and Loss Report', reportPeriod]),
      '',
      'SUMMARY FOR TAX PURPOSES',
      toCsvRow(['Net Sales Turnover (income excl. VAT)', `${currencySymbol}${safeFormatNumber(data.summary.totalIncome)}`]),
      toCsvRow(['Regular Expenses', `${currencySymbol}${safeFormatNumber(data.summary.totalRegularExpenses)}`]),
      toCsvRow(['Gross Net Profit (before capital allowances)', `${currencySymbol}${safeFormatNumber(data.summary.grossNetProfit)}`]),
      toCsvRow(['Annual Depreciation (capital allowances)', `${currencySymbol}${safeFormatNumber(data.summary.totalAnnualDepreciation)}`]),
      toCsvRow(['Adjusted Net Profit (taxable profit)', `${currencySymbol}${safeFormatNumber(data.summary.adjustedNetProfit)}`]),
      '',
      'NOTE: The Adjusted Net Profit figure above should be used for income tax purposes.',
      '',
      'ADDITIONAL INFORMATION',
      toCsvRow(['Capital Expenses (asset purchases)', `${currencySymbol}${safeFormatNumber(data.summary.totalCapitalExpenses)}`]),
      '',
      'INCOME BY CATEGORY (EXCLUDING VAT)',
      toCsvRow(['Category', 'Amount'])
    ];

    // Add income categories
    data.incomeCategories.forEach(category => {
      csvContent.push(toCsvRow([category.name, `${currencySymbol}${safeFormatNumber(category.amount)}`]));
    });

    csvContent.push('');
    csvContent.push('REGULAR EXPENSES BY CATEGORY');
    csvContent.push(toCsvRow(['Category', 'Amount']));

    // Add regular expense categories
    data.expenseCategories.forEach(category => {
      csvContent.push(toCsvRow([category.name, `${currencySymbol}${safeFormatNumber(category.amount)}`]));
    });

    // Add capital expense categories if any exist
    if (data.capitalExpenseCategories && data.capitalExpenseCategories.length > 0) {
      csvContent.push('');
      csvContent.push('CAPITAL EXPENSES BY CATEGORY (ASSET PURCHASES)');
      csvContent.push(toCsvRow(['Category', 'Amount']));

      data.capitalExpenseCategories.forEach(category => {
        csvContent.push(toCsvRow([category.name, `${currencySymbol}${safeFormatNumber(category.amount)}`]));
      });
    }

    csvContent.push('');
    csvContent.push('DETAILED TRANSACTIONS');
    csvContent.push(toCsvRow(['Date', 'Type', 'Category', 'Description', 'Amount', 'Asset Type']));

    // Add all transactions
    const allTransactions = [
      ...data.incomeCategories.flatMap(c => c.transactions),
      ...data.expenseCategories.flatMap(c => c.transactions),
      ...(data.capitalExpenseCategories ? data.capitalExpenseCategories.flatMap(c => c.transactions) : [])
    ].sort((a, b) => getDateSortTime(a.date) - getDateSortTime(b.date));

    allTransactions.forEach(transaction => {
      const date = formatDisplayDate(transaction.date, dateLocale);
      const typeKey = normalizeTransactionTypeKey(transaction.type);
      const type = typeKey ? typeKey.charAt(0).toUpperCase() + typeKey.slice(1) : 'Unknown';
      const categoryName = resolveCategoryName(typeKey, transaction.category || '');
      const reportAmount = typeKey === 'income'
        ? getTransactionIncomeNetAmount(transaction)
        : typeKey === 'expenses'
          ? getTransactionExpenseNetAmount(transaction)
        : (parseFloat(transaction.amount) || 0);
      
      // Determine if this is a capital expense
      const isCapitalAsset = typeKey === 'expenses' && (
        isCapitalExpense ? isCapitalExpense(transaction.category) : 
        ['equipment', 'furniture', 'electronics', 'salon_tools', 'vehicle'].includes(transaction.category)
      );
      
      csvContent.push(toCsvRow([
        date,
        type,
        categoryName,
        transaction.description || '',
        `${currencySymbol}${safeFormatNumber(reportAmount)}`,
        isCapitalAsset ? 'Capital Asset' : ''
      ]));
    });

    return csvContent.join('\n');
  };

  // Convert VAT report data to CSV
  const convertVatReportToCSV = (data) => {
    const currencySymbol = getCurrencySymbol();
    const reportPeriod = getReportPeriodLabel();

    // Create CSV content
    let csvContent = [
      toCsvRow(['VAT Report', reportPeriod]),
      '',
      'SUMMARY',
      toCsvRow(['VAT Collected on Sales', `${currencySymbol}${safeFormatNumber(data.summary.totalVatCollected)}`]),
      toCsvRow(['VAT Paid on Purchases', `${currencySymbol}${safeFormatNumber(data.summary.totalVatPaid)}`]),
      toCsvRow(['VAT Reclaimable', `${currencySymbol}${safeFormatNumber(data.summary.totalVatReclaimable)}`]),
      toCsvRow(['Prior-period adjustments', `${currencySymbol}${safeFormatNumber(data.summary.priorPeriodAdjustments)}`]),
      toCsvRow(['Net VAT Position', `${currencySymbol}${safeFormatNumber(data.summary.netVatPosition)}`]),
      '',
      'VAT BY RATE',
      toCsvRow(['Rate', 'Sales Amount', 'VAT on Sales', 'Purchases Amount', 'VAT on Purchases', 'Reclaimable VAT', 'Net VAT'])
    ];

    // Add VAT rate groups
    data.vatRateGroups.forEach(group => {
      const netVat = group.salesVat - group.reclaimableVat;
      csvContent.push(toCsvRow([
        group.rateFormatted,
        `${currencySymbol}${safeFormatNumber(group.salesAmount)}`,
        `${currencySymbol}${safeFormatNumber(group.salesVat)}`,
        `${currencySymbol}${safeFormatNumber(group.purchasesAmount)}`,
        `${currencySymbol}${safeFormatNumber(group.purchasesVat)}`,
        `${currencySymbol}${safeFormatNumber(group.reclaimableVat)}`,
        `${currencySymbol}${safeFormatNumber(netVat)}`
      ]));
    });

    if (Array.isArray(data.adjustments) && data.adjustments.length > 0) {
      csvContent.push('');
      csvContent.push('PRIOR-PERIOD ADJUSTMENTS');
      csvContent.push(toCsvRow([
        'Adjustment Date',
        'Original Date',
        'Type',
        'Category',
        'Description',
        'Previously Filed VAT',
        'Current VAT',
        'VAT Delta',
        'Net Return Effect'
      ]));

      data.adjustments.forEach((adjustment) => {
        const typeKey = normalizeTransactionTypeKey(adjustment.type);
        const isFiledReturnVariance = adjustment.source === 'filed_return_variance';
        const type = isFiledReturnVariance
          ? 'VAT correction'
          : (typeKey ? typeKey.charAt(0).toUpperCase() + typeKey.slice(1) : 'Unknown');
        const categoryName = isFiledReturnVariance
          ? 'Filed return'
          : resolveCategoryName(typeKey, adjustment.category || '');
        const netEffect = Number(adjustment.deltaSignedVatAmount) || 0;

        csvContent.push(toCsvRow([
          formatDisplayDate(adjustment.adjustmentDate, dateLocale),
          formatDisplayDate(adjustment.originalDate, dateLocale),
          type,
          categoryName,
          adjustment.description || '',
          `${currencySymbol}${safeFormatNumber(adjustment.previousVatAmount)}`,
          `${currencySymbol}${safeFormatNumber(adjustment.currentVatAmount)}`,
          `${currencySymbol}${safeFormatNumber(netEffect)}`,
          netEffect > 0 ? 'More VAT to pay' : 'More VAT to reclaim'
        ]));
      });
    }

    csvContent.push('');
    csvContent.push('DETAILED TRANSACTIONS');
    csvContent.push(toCsvRow(['Date', 'Type', 'Category', 'Description', 'Amount', 'VAT Rate', 'VAT Amount', 'Net Amount', 'Reclaimable']));

    // Add all transactions with VAT information
    const allTransactions = data.vatRateGroups.flatMap(g => g.transactions)
      .sort((a, b) => getDateSortTime(a.date) - getDateSortTime(b.date));

    allTransactions.forEach(transaction => {
      const date = formatDisplayDate(transaction.date, dateLocale);
      const typeKey = normalizeTransactionTypeKey(transaction.type);
      const type = typeKey ? typeKey.charAt(0).toUpperCase() + typeKey.slice(1) : 'Unknown';
      const vatEntry = transaction._vatEntry || buildTransactionVatEntries(transaction, { includeZeroVatEntries: true })[0];
      if (!vatEntry) return;

      const rowCategoryId = vatEntry.category || transaction.category || '';
      const categoryName = resolveCategoryName(typeKey, rowCategoryId);

      const grossAmount = parseFloat(vatEntry.grossAmount ?? transaction.amount) || 0;
      const vatAmount = parseFloat(vatEntry.vatAmount) || 0;
      const netAmount = grossAmount - vatAmount;
      const isReclaimable = transaction.vatReclaimable !== false;
      const displayCategoryName = !vatEntry.category && vatEntry.description
        ? `${categoryName} (${vatEntry.description})`
        : categoryName;
      
      csvContent.push(toCsvRow([
        date,
        type,
        displayCategoryName,
        transaction.description || '',
        `${currencySymbol}${safeFormatNumber(grossAmount)}`,
        formatVatRateLabel(vatEntry.rate),
        `${currencySymbol}${safeFormatNumber(vatAmount)}`,
        `${currencySymbol}${safeFormatNumber(netAmount)}`,
        isReclaimable ? 'Yes' : 'No'
      ]));
    });

    return csvContent.join('\n');
  };

  const convertFilteredSummaryToCSV = (data) => {
    const currencySymbol = getCurrencySymbol();
    const reportPeriod = getReportPeriodLabel();

    const csvContent = [
      toCsvRow(['Summary Report (Applied Filters)', reportPeriod]),
      '',
      'APPLIED FILTERS',
      toCsvRow(['Transaction type', appliedFiltersSummary.transactionType]),
      toCsvRow(['Categories', appliedFiltersSummary.categories]),
      toCsvRow(['Payment methods', appliedFiltersSummary.paymentMethods]),
      '',
      'SUMMARY',
      toCsvRow(['Transactions', data.summary.transactionCount]),
      toCsvRow(['Total Income', `${currencySymbol}${safeFormatNumber(data.summary.totalIncome)}`]),
      toCsvRow(['Total Expenses', `${currencySymbol}${safeFormatNumber(data.summary.totalExpenses)}`]),
      toCsvRow(['Net Position', `${currencySymbol}${safeFormatNumber(data.summary.netPosition)}`]),
      '',
      'INCOME BY CATEGORY',
      toCsvRow(['Category', 'Amount'])
    ];

    data.incomeCategories.forEach((entry) => {
      csvContent.push(toCsvRow([entry.name, `${currencySymbol}${safeFormatNumber(entry.value)}`]));
    });

    csvContent.push('');
    csvContent.push('EXPENSES BY CATEGORY');
    csvContent.push(toCsvRow(['Category', 'Amount']));
    data.expenseCategories.forEach((entry) => {
      csvContent.push(toCsvRow([entry.name, `${currencySymbol}${safeFormatNumber(entry.value)}`]));
    });

    csvContent.push('');
    csvContent.push('PAYMENT METHODS');
    csvContent.push(toCsvRow(['Payment Method', 'Amount']));
    data.paymentMethods.forEach((entry) => {
      csvContent.push(toCsvRow([entry.name, `${currencySymbol}${safeFormatNumber(entry.value)}`]));
    });

    return csvContent.join('\n');
  };

  const convertFilteredTransactionsToCSV = (filteredTransactions) => {
    const currencySymbol = getCurrencySymbol();
    const reportPeriod = getReportPeriodLabel();
    const csvContent = [
      toCsvRow(['Transactions Report (Applied Filters)', reportPeriod]),
      '',
      'APPLIED FILTERS',
      toCsvRow(['Transaction type', appliedFiltersSummary.transactionType]),
      toCsvRow(['Categories', appliedFiltersSummary.categories]),
      toCsvRow(['Payment methods', appliedFiltersSummary.paymentMethods]),
      '',
      toCsvRow(['Date', 'Type', 'Category', 'Description', 'Amount', 'Payment Method', 'VAT Rate', 'VAT Amount', 'Net Amount'])
    ];

    const sortedTransactions = [...filteredTransactions].sort((left, right) => {
      const leftDate = getDateSortTime(left?.date);
      const rightDate = getDateSortTime(right?.date);
      return leftDate - rightDate;
    });

    sortedTransactions.forEach((transaction) => {
      const typeKey = normalizeTransactionTypeKey(transaction?.type);
      if (typeKey !== 'income' && typeKey !== 'expenses') return;

      const date = formatDisplayDate(transaction?.date, dateLocale);
      const typeLabel = typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
      const categoryName = resolveCategoryName(typeKey, transaction?.category || '');
      const amount = Number(transaction?.amount || 0);

      const paymentBreakdown = Array.isArray(transaction?.paymentBreakdown)
        ? transaction.paymentBreakdown
            .map((entry) => ({
              method: normalizePaymentMethodId(entry?.method),
              amount: Number(entry?.amount) || 0
            }))
            .filter((entry) => entry.method && entry.amount > 0)
        : [];

      const paymentLabel = paymentBreakdown.length > 1
        ? paymentBreakdown
            .map((entry) => `${getPaymentMethodLabel(entry.method)} ${currencySymbol}${safeFormatNumber(entry.amount)}`)
            .join(' + ')
        : paymentBreakdown.length === 1
          ? getPaymentMethodLabel(paymentBreakdown[0].method)
          : getPaymentMethodLabel(transaction?.paymentMethod);

      const vatEntries = buildTransactionVatEntries(transaction, { includeZeroVatEntries: true });
      const normalizedVatRate = vatEntries.length === 1
        ? normalizeVatRate(vatEntries[0]?.rate)
        : null;
      const vatAmount = Number(getTransactionVatAmount(transaction) || 0);
      const netAmount = Number((amount - vatAmount).toFixed(2));

      csvContent.push(toCsvRow([
        date,
        typeLabel,
        categoryName,
        transaction?.description || '',
        `${currencySymbol}${safeFormatNumber(amount)}`,
        paymentLabel,
        normalizedVatRate !== null ? `${(normalizedVatRate * 100).toFixed(1)}%` : 'No VAT',
        `${currencySymbol}${safeFormatNumber(vatAmount)}`,
        `${currencySymbol}${safeFormatNumber(netAmount)}`
      ]));
    });

    return csvContent.join('\n');
  };

  // Download the selected report as CSV
  const downloadReport = () => {
    if (!user) {
      setShowAuthRequired(true);
      return;
    }
  
    try {
      if (reportType === 'profitLoss' && hasNonDateFiltersForProfitLoss) {
        alert('Profit & Loss export supports date range/period only. Please clear type/category/payment filters first.');
        return;
      }

      // Get filtered transactions based on criteria
      // For profit/loss and summary style reports, include both income and expenses from current view.
      const includeAllTypes = ['profitLoss', 'vat', 'filteredSummary', 'filteredTransactions'].includes(reportType);
      const filteredTransactions = getFilteredTransactions(includeAllTypes);
  
      // Check if there are transactions to export
      if (!filteredTransactions || filteredTransactions.length === 0) {
        alert('No transactions to export for the selected filters.');
        return;
      }
  
      // Generate report data based on selected report type
      let csvContent = '';
      let fileName = '';
      
      if (reportType === 'profitLoss') {
        const reportData = generateProfitLossData(filteredTransactions);
        csvContent = '\uFEFF' + convertProfitLossToCSV(reportData);
        fileName = `profit_loss_${selectedTimeframe || 'all'}.csv`;
      } else if (reportType === 'vat') {
        const reportData = generateVatReportData(filteredTransactions);
        csvContent = '\uFEFF' + convertVatReportToCSV(reportData);
        fileName = `vat_report_${selectedTimeframe || 'all'}.csv`;
      } else if (reportType === 'filteredSummary') {
        const reportData = generateFilteredSummaryData(filteredTransactions);
        csvContent = '\uFEFF' + convertFilteredSummaryToCSV(reportData);
        fileName = `summary_${selectedTimeframe || 'all'}.csv`;
      } else if (reportType === 'filteredTransactions') {
        csvContent = '\uFEFF' + convertFilteredTransactionsToCSV(filteredTransactions);
        fileName = `transactions_${selectedTimeframe || 'all'}.csv`;
      }
  
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
  
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
  
      alert(`Successfully exported ${(reportTypeLabels[reportType] || 'report')} to CSV.`);
    } catch (error) {
      console.error('[REPORT] Export error:', error);
      alert('An error occurred during export. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {allowedReportTypes.length > 1 && (
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-md text-sm"
          >
            {allowedReportTypes.map((type) => (
              <option key={type} value={type}>
                {reportTypeLabels[type] || type}
              </option>
            ))}
          </select>
        )}
        
        <button
          onClick={downloadReport}
          className="px-3 py-1 bg-[#E5FEFF] text-[color:var(--color-text)] rounded-md hover:opacity-90 transition text-sm flex items-center gap-2"
        >
          <Download className="w-4 h-4 text-[color:var(--color-text)]" />
          Export Selected
        </button>
      </div>

      <AuthCheck 
        isOpen={showAuthRequired}
        onClose={() => setShowAuthRequired(false)}
        onGoogleSignIn={handleGoogleSignIn}
      />
    </div>
  );
};

export default ReportExport;

